/**
 * "Is this the same fire?" — the human override.
 *
 * Whether two incidents are one fire is decided automatically from the fires'
 * mapped shapes and the resolution of the sensors that saw them. That is a
 * good guess, never more than that, and when it is wrong the consequence is
 * real: a fire folded into a neighbouring incident's monitoring shows no
 * agent of its own, and nothing on screen says why.
 *
 * This bar appears only when there is something to correct — the fire is
 * covered by another incident, or a person has already ruled on it — and lets
 * that person settle it. Marking it a separate fire starts its own monitoring
 * immediately on auto-monitored projects.
 */
import { useState } from 'react';
import { Alert, Box, Button, Snackbar, Typography } from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ReplayIcon from '@mui/icons-material/Replay';

import { IncidentSummary, useSetIncidentLink } from '../../api/incidentLog';

export default function IncidentLinkOverrideBar({
    incident,
    projectId,
}: {
    incident: IncidentSummary;
    projectId: string | number;
}) {
    const setLink = useSetIncidentLink(projectId);
    const [toast, setToast] = useState<string | null>(null);

    const coveredBy = incident.covered_by;
    const override = incident.link_override;
    // Nothing to correct: the automatic judgement gave this incident its own
    // monitoring and nobody has overruled anything.
    if (!coveredBy && !override) return null;
    // A closed incident is history; there is nothing left to monitor.
    if (incident.closed_at) return null;

    const markDifferent = () =>
        setLink.mutate(
            { incidentId: incident.id, override: 'different_fire' },
            {
                onSuccess: (res) =>
                    setToast(
                        res?.data?.agent_started
                            ? 'Marked as a separate fire — monitoring started for it.'
                            : 'Marked as a separate fire.'
                    ),
            }
        );

    const revert = () =>
        setLink.mutate(
            { incidentId: incident.id, override: '' },
            { onSuccess: () => setToast('Back to the automatic judgement.') }
        );

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    px: 1.5,
                    py: 1,
                    mb: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'action.hover',
                }}
            >
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
                    {override === 'different_fire'
                        ? `Marked as a separate fire${
                              incident.link_override_by ? ` by ${incident.link_override_by}` : ''
                          }, so it is monitored on its own.`
                        : override === 'same_fire'
                          ? `Marked as the same fire as #${coveredBy?.id}${
                                incident.link_override_by ? ` by ${incident.link_override_by}` : ''
                            }.`
                          : `This fire is already being monitored on incident #${coveredBy?.id}${
                                coveredBy?.project_name ? ` (${coveredBy.project_name})` : ''
                            }, so no separate monitoring was started here.`}
                </Typography>
                {override ? (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ReplayIcon />}
                        disabled={setLink.isPending}
                        onClick={revert}
                    >
                        Undo
                    </Button>
                ) : (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<LinkOffIcon />}
                        disabled={setLink.isPending}
                        onClick={markDifferent}
                    >
                        It&apos;s a different fire
                    </Button>
                )}
            </Box>
            <Snackbar
                open={!!toast}
                autoHideDuration={5000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setToast(null)} variant="filled">
                    {toast}
                </Alert>
            </Snackbar>
        </>
    );
}
